import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './AuthPages.module.css';
import navStyles from '../../components/layouts/PublicLayout.module.css';

const ForgotPasswordPage = () => {
  // Card 54619 -- the login form deep-links here with the refused address, so the
  // person who was just told "this account has no password" lands on a form that is
  // already filled in. A missing/blank param still falls back to an empty field.
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get('email') || '');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [scrolled, setScrolled] = useState(false);

  const { toast } = useToast();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    if (!email) {
      setErrors({ email: 'Email is required' });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      {/* Floating Navbar - same as login page */}
      <nav className={`${navStyles.navbar} ${scrolled ? navStyles.navbarScrolled : ''}`}>
        <Link to="/" className={navStyles.logo}>
          <img loading="lazy" src="/oval-logo.webp" alt="OVAL" className={navStyles.logoImg} />
        </Link>
        <div className={navStyles.navActions}>
          <Link to="/login" className={navStyles.signInBtn}>Sign In</Link>
          <Link to="/register" className={navStyles.signUpBtn}>Sign Up</Link>
        </div>
      </nav>

      {/* Left Side - Hero Image */}
      <div className={styles.heroSide}>
        <img
          src="/images/log-in-page/login-hero.webp"
          alt="Oval Care"
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              Care<br />
              that's Clear,<br />
              Not Loud
            </h1>
            <p className={styles.heroSubtitle}>
              Discover Oval's modern approach to wellness, where frosted clarity meets quiet confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className={styles.formSide}>
        <div className={styles.formContainer}>
          <div className={styles.formCard}>
            {!submitted ? (
              <>
                <div className={styles.formHeader}>
                  <h2 className={styles.formTitle}>Forgot Password?</h2>
                  <p className={styles.formSubtitle}>
                    Enter your email address and we'll send you instructions to reset your password.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                  <div className={styles.inputGroup}>
                    <div className={styles.inputWrapper}>
                      <span className={styles.inputIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                          <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                      </span>
                      <input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                        autoComplete="email"
                        autoFocus
                      />
                    </div>
                    {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
                  </div>

                  <button
                    type="submit"
                    className={styles.loginButton}
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>

                <div className={styles.formFooterLink}>
                  <span>Remember your password? </span>
                  <Link to="/login" className={styles.supportLink}>Back to Login</Link>
                </div>
              </>
            ) : (
              <div className={styles.successMessage}>
                <div className={styles.successIcon}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h2 className={styles.formTitle}>Check Your Email</h2>
                <p className={styles.formSubtitle}>
                  If an account exists for <strong>{email}</strong>, you'll receive an email with instructions to reset your password.
                </p>
                <p className={`${styles.formSubtitle} ${styles.successSubtext}`}>
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <div className={styles.successActions}>
                  <button
                    onClick={() => setSubmitted(false)}
                    className={`${styles.loginButton} ${styles.secondaryButton}`}
                  >
                    Try Again
                  </button>
                  <Link to="/login" className={styles.loginButton}>
                    Back to Login
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className={styles.pageFooter}>
            <span>© 2026 Oval Care Inc. All rights reserved.</span>
            <span className={styles.poweredBy}>
              Powered by <span className={styles.ovalLink}>Oval Care Inc.</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
